import { z } from 'zod'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import { omit } from 'es-toolkit'
import errorMessages from '/@lang/error-messages.json'
import type { AbstractId } from '#shared/types/utils'
import type { Locale } from '#shared/types/users.ts'

export function requiredError(
  errorMessage?: string,
  errorType?: string
): {
  error: string
} {
  const localeIsDutch = useLocaleStore().localeIsDutch
  const fieldIsRequiredMessage = localeIsDutch
    ? 'Dit veld is verplicht'
    : 'This field is required'
  const errorObj = { error: errorMessage ?? fieldIsRequiredMessage }

  if (errorType) {
    Object.assign(errorObj, {
      error: localeIsDutch ? `Vul een ${errorType} in` : `Fill in a ${errorType}`
    })
  }

  return errorObj
}

type ErrorMessages = Record<Locale, Record<string, string>>

export function getErrorMessage(keyPath: string = 'option-is-required') {
  const messages: ErrorMessages = errorMessages
  return {
    error: () => {
      const locale = useLocaleStore().locale
      return messages[locale][keyPath]
    }
  }
}

export const zodErrors = computed(() => {
  const localeIsDutch = useLocaleStore().localeIsDutch
  const amountType = localeIsDutch ? 'aantal' : 'amount'
  const dateType = localeIsDutch ? 'datum' : 'date'

  return {
    required: requiredError,
    min: (amount: number, type: string = amountType) =>
      localeIsDutch
        ? `Het ${type} moet minimaal ${amount} zijn`
        : `The ${type} must be at least ${amount}`,
    max: (amount: number, type: string = amountType) =>
      localeIsDutch
        ? `Het ${type} mag maximaal ${amount} zijn`
        : `The ${type} can be at most ${amount}`,
    maxString: (amount: number, type: string = 'De tekst') =>
      localeIsDutch
        ? `${type} mag maximaal ${amount} karakters lang zijn`
        : `The ${type} can be at most ${amount} characters long`,
    emptyString: (type?: string) => {
      if (type) {
        return localeIsDutch ? `Vul een ${type} in` : `Fill in a ${type}`
      }

      return requiredError().error
    },
    int: () => (localeIsDutch ? 'Vul een geheel getal in' : 'Enter an integer'),
    number: () => (localeIsDutch ? 'Vul een getal in' : 'Enter a number'),
    date: (type: string = dateType) =>
      localeIsDutch ? `Vul een ${type} in` : `Fill in a ${type}`,
    email: () =>
      localeIsDutch
        ? 'Dit is een ongeldig e-mailadres'
        : 'This is an invalid email address',
    emoji: () => (localeIsDutch ? 'Voer een emoji in' : 'Enter an emoji'),
    multipleOf: (amount: number | bigint) =>
      localeIsDutch
        ? `Voer een veelvoud van ${amount} in`
        : `Enter a multiple of ${amount}`
  }
})

// Source: https://github.com/colinhacks/zod/discussions/1259
const DayJSZod = dayjs as unknown as typeof Dayjs

export const dayJSZod = (message?: string) =>
  z.instanceof(DayJSZod, {
    error: message ?? zodErrors.value.date()
  })

export const nullableDayJSZod = (message?: string) =>
  z
    .instanceof(DayJSZod, {
      error: message ?? zodErrors.value.date()
    })
    .nullable()

/**
 * Removes keys specified by the keys variable. For example, can be used to remove the `id` field.
 * Returns an object that is ready to use with the `resetForm` function from vee-validate.
 *
 * Warning: The return type is destructive, so you lose autocompletion, do not use the returned
 * object for anything but the `resetForm` function.
 *
 * @param model
 * @param keys
 */
export function removeKeysForResetForm<T extends object>(
  model: T,
  keys: (keyof T)[]
): { values: Omit<T, keyof T> } {
  return {
    values: omit(model, keys)
  }
}

export function idValidation<T extends AbstractId<string>>(message?: string) {
  return z.custom<T>((value) => typeof value === 'number', {
    error: message
  })
}
